import { ArchiveHistoryFolder, ArchiveHistoryV2, EmailData, FolderItem, MinimalEmailData, Project } from "../../common/src/types/types";
import { setEmailCategory } from "./graphClient";
//import { enqueueCategoryUpdate } from "./categoryQueue";

const API_BASE_URL = (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') ? "http://localhost:3000/api" : "https://efile.tlceng.cloud/api"; // Update this to actual API base URL in production

let accessTokenCache: { token: string, expiration: number } | null = null;

async function getAccessToken(): Promise<string> {

    // Retry logic in case we get rate limited or transient errors
    for (let tries = 0; tries < 3; tries++) {
        try {
            if (accessTokenCache && accessTokenCache.expiration > Date.now() + 60000) {
                return accessTokenCache.token;
            }
            const result = await Office.auth.getAccessToken();
            accessTokenCache = {
                token: result,
                expiration: Date.now() + 3600000, // 1 hour
            };
            return result;
        } catch (error) {
            if (tries === 2) {
                console.error("Failed to get access token:", error);
                throw new Error("Authentication failed. Please sign in again.");
            }
        }
        await new Promise(res => setTimeout(res, 1000 * tries));
    }

    throw new Error("Authentication failed. Please sign in again.");
}

export async function getAccessTokenForDialog(): Promise<string> {
    return getAccessToken();
}

async function apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    for (let tries = 0; tries < 3; tries++) {
        try {
            const token = await getAccessToken();
            const defaultOptions: RequestInit = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    ...options.headers,
                },
            };

            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                ...defaultOptions,
                ...options,
            });

            if (!response.ok) {
                if (response.status === 401) {
                    accessTokenCache = null;
                }
                throw new Error(`API call failed: ${response.status} ${response.statusText}`);
            }
            return await response.json() as T;

        } catch (error) {
            console.error("API call error:", error);
            if (tries === 2) {
                throw error;
            }
            await new Promise(res => setTimeout(res, 1000 * tries)); // Exponential backoff
        }
    }

    throw new Error("API call failed after multiple attempts");
}

export interface ClientConfig {
    projectRootPath: string;
}

let clientConfigPromise: Promise<ClientConfig> | null = null;

export function getClientConfig(): Promise<ClientConfig> {
    if (!clientConfigPromise) {
        clientConfigPromise = apiCall<ClientConfig>("/config").catch((error) => {
            clientConfigPromise = null;
            throw error;
        });
    }
    return clientConfigPromise;
}

export async function getProjects(term?: string): Promise<Project[]> {
    try {
        const query = term?.trim() ? `?term=${encodeURIComponent(term.trim())}` : "";
        const response = await apiCall<{ projects: Project[] }>(`/projects${query}`);
        return response.projects || [];
    } catch (error) {
        console.error("Failed to fetch projects:", error);
        return [];
    }
}

export async function getUserFavorites(): Promise<FolderItem[]> {
    try {
        return await apiCall<FolderItem[]>("/user/favorites");
    } catch (error) {
        console.error("Failed to fetch user favorites:", error);
        return [];
    }
}

export async function addUserFavorite(folder: FolderItem): Promise<void> {
    try {
        await apiCall<void>("/user/favorites", {
            method: "POST",
            body: JSON.stringify(folder),
        });
    } catch (error) {
        console.error("Failed to add user favorite:", error);
    }
}

export async function removeUserFavorite(folderId: string): Promise<void> {
    try {
        await apiCall<void>(`/user/favorites/${folderId}`, {
            method: "DELETE",
        });
    } catch (error) {
        console.error("Failed to remove user favorite:", error);
    }
}

export async function getFolderEmails(folderId: string): Promise<EmailData[]> {
    try {
        return await apiCall<EmailData[]>(`/folders/${folderId}/emails`);
    } catch (error) {
        console.error("Failed to fetch folder emails:", error);
        return [];
    }
}

export async function saveEmailsToFolder(folderId: string, emails: MinimalEmailData[], deleteAfterArchive: boolean = false, retrying: boolean = false): Promise<void> {
    try {
        console.log("Saving emails to folder:", folderId, "Emails:", emails);
        await apiCall<void>(`/folders/${folderId}/emails`, {
            method: "POST",
            body: JSON.stringify({ emails: emails, deleteAfterArchive: deleteAfterArchive, retrying: retrying }),
        });

        // for (const email of emails) {
        //     enqueueCategoryUpdate(email.id, "E-File: Queued");
        // }
    } catch (error) {
        console.error("Failed to save emails to folder:", error);
    }
}

export async function removeEmailBody(emailId: string): Promise<void> {
    try {
        const encodedEmailId = encodeURIComponent(emailId);
        await apiCall<void>(`/emails/${encodedEmailId}/removeBody`, {
            method: "POST",
        });
    } catch (error) {
        console.error("Failed to remove email body:", error);
    }
}

export async function deleteEmail(emailId: string): Promise<void> {
    try {
        const encodedEmailId = encodeURIComponent(emailId);
        await apiCall<void>(`/emails/${encodedEmailId}`, {
            method: "DELETE",
        });
        await setEmailCategory(emailId, "");
    } catch (error) {
        console.error("Failed to delete email:", error);
    }
}

export async function getArchiveHistory(): Promise<ArchiveHistoryV2[]> {
    try {
        return await apiCall<ArchiveHistoryV2[]>("/user/archiveHistoryV2");
    } catch (error) {
        console.error("Failed to fetch archive history:", error);
        return [];
    }
}

export async function addFolderKeywords(folderId: string, keywords: string[]): Promise<void> {
    try {
        await apiCall<void>(`/folders/${folderId}/keywords`, {
            method: "POST",
            body: JSON.stringify({ keywords }),
        });
    } catch (error) {
        console.error("Failed to add folder keywords:", error);
    }
}

export async function removeFolderKeyword(folderId: string, keyword: string): Promise<void> {
    try {
        await apiCall<void>(`/folders/${folderId}/keywords`, {
            method: "DELETE",
            body: JSON.stringify({ keyword }),
        });
    } catch (error) {
        console.error("Failed to remove folder keyword:", error);
    }
}

export async function updateFolderKeywords(folderId: string, keywords: string[]): Promise<void> {
    try {
        await apiCall<void>(`/folders/${folderId}/keywords`, {
            method: "PUT",
            body: JSON.stringify({ keywords }),
        });
    } catch (error) {
        console.error("Failed to update folder keywords:", error);
        throw error;
    }
}

export async function updateFolderBodyStoragePreference(folderId: string, doNotStoreBody: boolean): Promise<void> {
    try {
        await apiCall<void>(`/folders/${folderId}/bodyStoragePreference`, {
            method: "PUT",
            body: JSON.stringify({ doNotStoreBody }),
        });
    } catch (error) {
        console.error("Failed to update folder body storage preference:", error);
        throw error;
    }
}

export async function setFolderHiddenStatus(folderId: string, hidden: boolean): Promise<void> {
    try {
        await apiCall<void>(`/user/favorites/${folderId}/hidden`, {
            method: "POST",
            body: JSON.stringify({ hidden }),
        });
    } catch (error) {
        console.error("Failed to update folder hidden status:", error);
        throw error;
    }
}

export async function syncLegacyFolder(folderId: string): Promise<void> {
    try {
        await apiCall<void>(`/folders/${folderId}/syncLegacy`, {
            method: "POST",
        });
    } catch (error) {
        console.error("Failed to flag folder for legacy sync:", error);
        throw error;
    }
}

export async function getEmailBody(emailId: string, accessToken?: string): Promise<{ body: string; bodyRemoved: boolean }> {
    try {
        const encodedEmailId = encodeURIComponent(emailId);
        if (accessToken) {
            const response = await fetch(`${API_BASE_URL}/emails/${encodedEmailId}/body`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`API call failed: ${response.status} ${response.statusText}`);
            }

            return await response.json() as { body: string; bodyRemoved: boolean };
        }

        return await apiCall<{ body: string; bodyRemoved: boolean }>(`/emails/${encodedEmailId}/body`);
    } catch (error) {
        console.error("Failed to fetch email body:", error);
        return { body: "", bodyRemoved: true };
    }
}

export async function searchEmails(term: string, folderId: string): Promise<EmailData[]> {
    try {
        const query = term.trim() ? `?term=${encodeURIComponent(term.trim())}` : "";
        return await apiCall<EmailData[]>(`/folders/${folderId}/emails/search${query}`);
    } catch (error) {
        console.error("Failed to search emails:", error);
        return [];
    }
}

export async function changeFavoriteName(folderId: string, newName: string): Promise<void> {
    try {
        const response = await apiCall<void>(`/user/favorites/${folderId}/name`, {
            method: "POST",
            body: JSON.stringify({ name: newName }),
        });
    } catch (error) {
        console.error("Failed to change favorite name:", error);
        throw error;
    }
}

export async function removeFavoriteName(folderId: string): Promise<string> {
    try {
        const response = await apiCall<{ name: string }>(`/user/favorites/${folderId}/name`, {
            method: "DELETE",
        });
        return response.name;
    } catch (error) {
        console.error("Failed to remove favorite name:", error);
        throw error;
    }
}